import MiniAppContainer from "./MiniAppContainer";
import MiniAppHeader from "./layout/MiniAppHeader";
import { useDispatch, useSelector } from "react-redux";
import TradeAssistantCard from "../trading-assistant/assistant/TradeAssistantCard";
import React, { useEffect, useMemo, useState } from "react";
import useRefreshSignals from "../../hooks/api/useRefreshSignals";
import useLiveSuggestionRefresh from "../../hooks/api/ai-assistant/useLiveSuggestionRefresh";
import usePriceRefresh from "../../hooks/api/ai-assistant/usePriceRefresh";
import useSortedSignals from "../../hooks/api/ai-assistant/useSortedSignals";
import styles from "../../styles/Components/mini-app/mini-ai-signals/MiniAiSignals.module.css";
import { Dropdown, Skeleton } from "antd";
import {
  getTradeAssistant,
  setAIMarketType,
} from "../../redux/trading-assistant/actions";
import useServiceStatus from "../../hooks/useServiceStatus";
import { FaAngleDown, FaBasketShopping } from "react-icons/fa6";
import { marketItems } from "../trading-assistant/assistant/AiAssistantTableActionButtons";
import { isStockBoard, sortStockCards, stockBoardCards, stockBoardLabel, stockPivotTargets } from "../../shared/stockBoards";
import capitalize from "antd/es/_util/capitalize";
import { useSearchParams } from "react-router-dom";
import { IoChevronBackOutline } from "react-icons/io5";
import MiniLatestSignals from "./MiniLatestSignals";
import { FaTelegramPlane } from "react-icons/fa";

function MiniAiSignals() {
  const {
    tradingAssistant: {
      getTradeAssistantLoading,
      tradeAssistant,
      aiMarketType,
      allTradeSuggestionData,
      viewType,
      aiAssistantMode,
    },
    main: { userData },
    liveSuggestion: { suggestions, usPreset },
    otherCoinsAnalyze: { analyze },
    profile: { profileData, loading },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const stopLossCFC = tradeAssistant?.coefficients?.stop_loss;
  const takeProfitCFC = tradeAssistant?.coefficients?.take_profit;

  const sortedCardsData = useSortedSignals({
    data: tradeAssistant?.ASSISTANT_SUGGESTIONS,
    allTradeSuggestionData,
    analyze,
    suggestions,
    aiMarketType,
  });

  const [cardsData, setCardsData] = useState([]);
  const [fetchSignals, setFetchSignals] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("view");

  const symbols = cardsData
    ?.filter((item) => item?.SYMBOL_NAME)
    ?.map((item) => item?.SYMBOL_NAME?.toLowerCase()?.split("/usdt")?.[0]);

  const { isActive } = useServiceStatus("aiAssistant");

  const marketMenu = {
    items: marketItems,
    defaultSelectedKeys: [aiMarketType || "null"],
    selectedKeys: [aiMarketType || "null"],
    onClick: (e) => {
      dispatch(setAIMarketType(e.key));
      if (isStockBoard(e.key)) return;
      dispatch(
        getTradeAssistant(
          userData?.["cognito:username"],
          userData?.["custom:custom_username"],
          "primary",
          e.key,
          isActive,
          aiAssistantMode,
        ),
      );
    },
  };

  const viewTelegramSignals = () => {
    searchParams.set("view", "telegram_signals");
    setSearchParams(searchParams);
  };

  useEffect(() => {
    setCardsData(sortedCardsData);
  }, [sortedCardsData]);

  // Update prices every 60s
  usePriceRefresh({
    cardsData,
    stopLossCFC,
    takeProfitCFC,
    userData,
    setCardsData,
    active: aiMarketType === "crypto",
  });

  const stockBoard = isStockBoard(aiMarketType);
  const stockRows = stockBoard ? stockBoardCards(aiMarketType, [], usPreset) : [];
  const pivotTargets = useMemo(() => {
    if (stockBoard) return stockPivotTargets(aiMarketType, [], usPreset);
    if (aiMarketType !== "crypto") return [];
    const seen = new Set();
    const targets = [];
    [...(tradeAssistant?.ASSISTANT_SUGGESTIONS || []), ...(analyze || [])].forEach(
      (item) => {
        const symbol = item?.SYMBOL_NAME;
        if (!symbol) return;
        const key = String(symbol).split("/")[0].toUpperCase();
        if (seen.has(key)) return;
        seen.add(key);
        targets.push({
          symbol,
          exchange: item?.AVAILABLE_EXCHANGES?.[0] || item?.EXCHANGE_ID || "kucoin",
        });
      },
    );
    return targets;
  }, [aiMarketType, analyze, stockBoard, tradeAssistant?.ASSISTANT_SUGGESTIONS, usPreset]);
  useLiveSuggestionRefresh(aiMarketType === "crypto" || stockBoard, pivotTargets);

  useRefreshSignals("ai");

  useEffect(() => {
    if (
      userData?.["cognito:username"] &&
      allTradeSuggestionData?.length === 0 &&
      !tradeAssistant?.risk_taking &&
      profileData?.email &&
      !fetchSignals &&
      !isStockBoard(aiMarketType)
    ) {
      dispatch(
        getTradeAssistant(
          userData?.["cognito:username"],
          userData?.["custom:custom_username"],
          "primary",
          aiMarketType,
          isActive,
        ),
      );
      setFetchSignals(true);
    }
  }, [
    dispatch,
    userData,
    aiMarketType,
    profileData,
    isActive,
    allTradeSuggestionData,
    tradeAssistant,
  ]);

  return (
    <MiniAppContainer>
      <MiniAppHeader page={"AI Signals"}>
        {viewMode !== "telegram_signals" ? (
          <span className={styles.headerAction} onClick={viewTelegramSignals}>
            <FaTelegramPlane />
            Telegram signals
          </span>
        ) : (
          <span className={styles.headerAction} onClick={() => setSearchParams("")}>
            <IoChevronBackOutline />
          </span>
        )}
      </MiniAppHeader>
      {viewMode !== "telegram_signals" ? (
        <div className={styles.markets}>
          <div className={styles.title}>
            <FaBasketShopping />
            Markets
          </div>
          <span className={styles.separator}></span>
          <Dropdown
            menu={marketMenu}
            rootClassName={styles.dropdownMenu}
            disabled={!!getTradeAssistantLoading || !!loading}
          >
            <span
              className={styles.currentMarket}
              style={!!getTradeAssistantLoading ? { cursor: "progress" } : {}}
            >
              {stockBoardLabel(aiMarketType) || capitalize(aiMarketType)}
              <FaAngleDown size={12} />
            </span>
          </Dropdown>
        </div>
      ) : null}
      {viewMode !== "telegram_signals" ? (
        getTradeAssistantLoading && !stockBoard || (!stockBoard && cardsData?.length === 0) ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton.Node className={styles.loader} key={i} active />
          ))
        ) : (
          (stockBoard ? sortStockCards(stockRows, suggestions) : cardsData).map((item, index) => (
            <TradeAssistantCard data={item} key={index} />
          ))
        )
      ) : (
        <MiniLatestSignals />
      )}
    </MiniAppContainer>
  );
}

export default MiniAiSignals;
